import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useParams } from 'react-router';
import { connect } from 'react-redux';
import { Container } from '../../../../ui/gridSystem';
import {
  CTA,
  CustomInput,
  CustomSelect,
  Icon,
  NoResult,
  ProgressBar,
  Text,
} from '../../../../ui/atoms';
import { withMediaQueries } from '../../../hoc/withMediaQueries';
import {
  COURSE_USERS_ACTIVITIES_RESET,
  EVENT_USERS_ACTIVITIES_GET,
  EVENT_USERS_GET,
} from '../../../redux/actions/statistics';
import SortBy from '../../../../ui/atoms/SortBy';
import Pagination from '../../../../ui/atoms/Pagination';
import {
  CardsWrapper,
  CompletionWrapper,
  FiltersWrapper,
  Legend,
  SortingWrapper,
  UserCard,
} from './style';
import theme from '../../../../ui/theme';
import CustomTooltip from '../../../../ui/atoms/CustomTooltip';
import {
  getFilterLabel,
  getFilterOptions,
  getSortingOptions,
} from '../../../../utils/labels';
import routes from '../../../../routes';
import { timestampToDate } from '../../../../utils/common';
import ActivitiesStats from '../../../../ui/components/ActivitiesStats';
import TimeIntervalFilter from '../../../../ui/atoms/TimeIntervalFilter';

const Users = ({
  users,
  activities,
  filters,
  pager,
  getCourseUsers,
  getCourseUsersActivities,
  courseUserActivitiesReset,
}) => {
  const { course_id } = useParams();
  const [activeCardId, setActiveCardId] = useState();
  const navigate = useNavigate();

  // filters
  const [selectedFilters, setSelectedFilters] = useState();

  useEffect(() => {
    getCourseUsers({ filters_only: true }, course_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onClickSearch = () => {
    getCourseUsers(selectedFilters, course_id);
  };

  const handleOnChange = ({ key, value }) => {
    const newFilters = { ...selectedFilters };
    newFilters[key] = value?.value;
    setSelectedFilters(newFilters);
    if (key === 'ffoo' || key === 'regions') {
      getCourseUsers({ ...newFilters, filters_only: true }, course_id);
    }
  };

  const loadMore = (page) =>
    getCourseUsers({ ...selectedFilters, page }, course_id);
  const setItemsPerPage = (v) =>
    handleOnChange({ key: 'items_per_page', value: v });

  const onClickCard = (user_id) => {
    courseUserActivitiesReset();
    if (user_id === activeCardId) {
      setActiveCardId(null);
    } else {
      setActiveCardId(user_id);
      getCourseUsersActivities(course_id, user_id);
    }
  };

  const isMandatory = (key) => {
    if (key === 'ffoo' /* || key === 'group_ids'*/) return 'mandatory-select';
    return '';
  };

  return (
    <Container className="pt-0">
      <Text type="h2" mb={34}>
        Report utenti
      </Text>
      <FiltersWrapper>
        <CustomInput
          size={426}
          placeholder="Cosa vuoi cercare?..."
          isSearch
          value=""
          icon="search-sm"
          isClearable
          onChange={(value) =>
            handleOnChange({ key: 'search_text', value: { value } })
          }
          onClickIcon={onClickSearch}
          onKeyPress={(event) => {
            if (event.key === 'Enter') onClickSearch();
          }}
        />
        {(Object.keys(filters) || []).map((key, i) => (
          <CustomSelect
            size="s"
            key={`filter-${key}-${i}`}
            wrapperId={`filter-${key}`}
            placeholder={getFilterLabel(key)}
            options={getFilterOptions(filters, key)}
            onChange={(value) => handleOnChange({ key, value })}
            isClearable
            className={isMandatory(key)}
          />
        ))}
        <TimeIntervalFilter
          callback={(start_date, end_date) => {
            setSelectedFilters({
              ...selectedFilters,
              start_date,
              end_date,
            });
          }}
        />
        <CTA
          id="search-cta"
          btnText="Cerca"
          disabled={/*!selectedFilters?.group_ids || */ !selectedFilters?.ffoo}
          onClick={() => getCourseUsers(selectedFilters, course_id)}
        />
      </FiltersWrapper>
      <SortingWrapper>
        <Text type="b3" className="info">
          <Icon type="info-circle" clickable={false} />
          Applica i filtri obbligatori per effettuare la ricerca
        </Text>
        <SortBy
          options={getSortingOptions([
            'alphabetical_asc',
            'alphabetical_desc',
            'quiz_result_asc',
            'quiz_result_desc',
          ])}
          onChange={(v) => {
            const newFilters = { ...selectedFilters, sort_by: v?.value };
            setSelectedFilters(newFilters);
            if (newFilters?.ffoo) getCourseUsers(newFilters, course_id);
          }}
        />
      </SortingWrapper>
      {users?.length > 0 ? (
        <>
          <Legend>
            <Text type="statistics-heading">Utenti</Text>
            <Text type="statistics-heading">Forza dell'ordine</Text>
            <Text type="statistics-heading">Ruolo</Text>
            <CompletionWrapper>
              <Text type="statistics-heading">Completamento</Text>
              <CustomTooltip
                type="primary"
                title="Completamento"
                tooltipKey="course_completion"
              />
            </CompletionWrapper>
            <Text type="statistics-heading">Data inizio percorso</Text>
            <Text type="statistics-heading">Data fine percorso</Text>
          </Legend>
          <CardsWrapper hasPaginator={pager?.number_of_pages > 1}>
            {users.map((user, i) => {
              const { completion } = user?.course_stats || {};
              return (
                <React.Fragment key={`user-${user?.id}`}>
                  <UserCard>
                    <Text
                      type="h3"
                      className="ellipsis"
                      onClick={() =>
                        navigate(`${routes.userStatistics.path}/${user?.id}`)
                      }
                    >
                      {user?.name} {user?.surname}
                    </Text>
                    <Text type="b4" className="ellipsis">
                      {user?.ffoo || '-'}
                    </Text>
                    <Text type="b4" className="ellipsis">
                      {user?.work_role || '-'}
                    </Text>
                    <ProgressBar
                      percentage={
                        completion?.rate ? Math.round(completion.rate * 100) : 0
                      }
                      hasPercentage
                      bgColor="#D1D1D1"
                      color={theme().colors.darkerBlue}
                      height={8}
                      rearrange
                    />
                    <Text type="b4">
                      {timestampToDate(completion?.start_date) || '-'}
                    </Text>
                    <Text type="b4">
                      {timestampToDate(completion?.completion_end) || '-'}
                    </Text>
                    <CTA
                      type="tertiary"
                      onClick={() => {
                        onClickCard(user?.id);
                      }}
                      btnText={user?.id === activeCardId ? 'Chiudi' : 'Esplora'}
                      icon={user?.id === activeCardId ? 'minus' : 'plus'}
                    />
                  </UserCard>
                  {user?.id === activeCardId && (
                    <ActivitiesStats activities={activities} isEvent />
                  )}
                </React.Fragment>
              );
            })}
          </CardsWrapper>
          <Pagination
            pager={pager}
            callBack={loadMore}
            setItemsPerPage={setItemsPerPage}
          />
        </>
      ) : (
        <NoResult />
      )}
    </Container>
  );
};

Users.propTypes = {
  // HOC: state
  users: PropTypes.array,
  activities: PropTypes.array,
  filters: PropTypes.object,
  pager: PropTypes.object,
  //HOC: dispatch
  getCourseUsers: PropTypes.func,
  getCourseUsersActivities: PropTypes.func,
  courseUserActivitiesReset: PropTypes.func,
};

export default connect(
  (state) => {
    const {
      users: { list: users, activities, filters, pager },
    } = state.statistics.course;
    return {
      users,
      activities,
      filters,
      pager,
    };
  },
  (dispatch) => ({
    getCourseUsers: (filters, course_id) =>
      dispatch({ type: EVENT_USERS_GET._REQUEST, filters, course_id }),
    getCourseUsersActivities: (course_id, user_id) =>
      dispatch({
        type: EVENT_USERS_ACTIVITIES_GET._REQUEST,
        course_id,
        user_id,
      }),
    courseUserActivitiesReset: () =>
      dispatch({ type: COURSE_USERS_ACTIVITIES_RESET }),
  })
)(withMediaQueries(Users));
